import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import { mockPosts } from '../../../mocks/posts';
import { mockReplies } from '../../../mocks/replies';
import { mockPartners } from '../../../mocks/partners';
import RelatedLinks from '../../../components/feature/RelatedLinks';

interface Post {
  id: string;
  user_id: string;
  category: string;
  title: string;
  content: string;
  need_partner_response: boolean;
  empathy_count: number;
  created_at: string;
  profiles?: {
    nickname: string;
    user_type: string;
    partner_role?: string;
    avatar_url?: string;
  };
}

interface Reply {
  id: string;
  post_id: string;
  user_id: string;
  content: string;
  created_at: string;
  profiles?: {
    nickname: string;
    user_type: string;
    partner_role?: string;
    avatar_url?: string;
  };
  like_count?: number;
  empathy_count?: number;
  user_liked?: boolean;
  user_empathized?: boolean;
}

interface ReactionCount {
  reply_id: string;
  like_count: number;
  empathy_count: number;
}

export default function PostDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [post, setPost] = useState<Post | null>(null);
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [empathized, setEmpathized] = useState(false);
  const [empathyCount, setEmpathyCount] = useState(0);
  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [submittingEmpathy, setSubmittingEmpathy] = useState(false);
  const [isPostInDatabase, setIsPostInDatabase] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [reactionLoading, setReactionLoading] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchPost();
      fetchReplies();
      if (user) {
        checkEmpathy();
      }
    }
  }, [id, user]);

  const fetchPost = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      
      // データベースに投稿がない場合はモックデータを使用
      if (!data) {
        const mockPost = mockPosts.find(p => p.id === id);
        if (mockPost) {
          setPost({
            id: mockPost.id,
            user_id: mockPost.author.id,
            category: mockPost.category || 'つぶやき・モヤモヤ',
            title: mockPost.title,
            content: mockPost.content,
            need_partner_response: false,
            empathy_count: mockPost.empathy_count,
            created_at: mockPost.created_at,
            profiles: {
              nickname: mockPost.author.nickname,
              user_type: mockPost.author.user_type,
              partner_role: mockPost.author.partner_role,
              avatar_url: mockPost.author.avatar_url,
            },
          });
          setEmpathyCount(mockPost.empathy_count);
          setIsPostInDatabase(false);
        }
        setLoading(false);
        return;
      }
      
      let profileData = null;

      if (data.user_id) {
        // 実ユーザーの場合はDBからプロフィール取得
        const { data: pData } = await supabase
          .from('profiles')
          .select('nickname, user_type, partner_role, avatar_url')
          .eq('id', data.user_id)
          .maybeSingle();
        profileData = pData;
      }

      // DBにプロフィールがない場合、またはニックネームがない場合、モックデータから補完
      if (!profileData || !profileData.nickname) {
        const mockPost = mockPosts.find(m => m.id === data.id);
        if (mockPost) {
          profileData = {
            nickname: profileData?.nickname || mockPost.author.nickname,
            user_type: profileData?.user_type || mockPost.author.user_type,
            partner_role: profileData?.partner_role || mockPost.author.partner_role,
            avatar_url: profileData?.avatar_url || mockPost.author.avatar_url,
          };
        }
      }
      
      setPost({
        ...data,
        profiles: profileData || undefined,
      });
      setEmpathyCount(data?.empathy_count || 0);
      setIsPostInDatabase(true);
    } catch (error) {
      console.error('投稿の取得エラー:', error);
      // エラーの場合もモックデータを試す
      const mockPost = mockPosts.find(p => p.id === id);
      if (mockPost) {
        setPost({
          id: mockPost.id,
          user_id: mockPost.author.id,
          category: mockPost.category || 'つぶやき・モヤモヤ',
          title: mockPost.title,
          content: mockPost.content,
          need_partner_response: false,
          empathy_count: mockPost.empathy_count,
          created_at: mockPost.created_at,
          profiles: {
            nickname: mockPost.author.nickname,
            user_type: mockPost.author.user_type,
            partner_role: mockPost.author.partner_role,
            avatar_url: mockPost.author.avatar_url,
          },
        });
        setEmpathyCount(mockPost.empathy_count);
        setIsPostInDatabase(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReplies = async () => {
    try {
      const { data, error } = await supabase
        .from('replies')
        .select('*')
        .eq('post_id', id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      
      // データベースに返信がない場合はモックデータを使用
      if (!data || data.length === 0) {
        const mockRepliesForPost = mockReplies.filter(r => r.post_id === id);
        // モックデータにリアクション情報を追加（Supabaseから取得を試みる）
        const mockWithReactions = await Promise.all(
          mockRepliesForPost.map(async (reply) => {
            const reactionData = await fetchReactionCounts(reply.id);
            const userReactions = user ? await fetchUserReactions(reply.id) : { liked: false, empathized: false };
            return {
              ...reply,
              like_count: reactionData.like_count,
              empathy_count: reactionData.empathy_count,
              user_liked: userReactions.liked,
              user_empathized: userReactions.empathized,
            };
          })
        );
        setReplies(mockWithReactions);
        return;
      }
      
      // 各返信のプロフィール情報とリアクション情報を取得
      const repliesWithProfiles = await Promise.all(
        data.map(async (reply) => {
          let profileData = null;

          if (reply.user_id) {
            const { data: pData } = await supabase
              .from('profiles')
              .select('nickname, user_type, partner_role, avatar_url')
              .eq('id', reply.user_id)
              .maybeSingle();
            profileData = pData;
          }

          // DBにプロフィールがない場合、またはニックネームがない場合、モックデータから補完
          if (!profileData || !profileData.nickname) {
            const mockReply = mockReplies.find(m => m.id === reply.id);
            if (mockReply) {
              profileData = {
                nickname: profileData?.nickname || mockReply.profiles.nickname,
                user_type: profileData?.user_type || mockReply.profiles.user_type,
                partner_role: profileData?.partner_role || (mockReply.profiles as any).partner_role,
                avatar_url: profileData?.avatar_url || mockReply.profiles.avatar_url,
              };
            }
          }

          // リアクション情報を取得
          const reactionData = await fetchReactionCounts(reply.id);
          const userReactions = user ? await fetchUserReactions(reply.id) : { liked: false, empathized: false };
          
          return {
            ...reply,
            profiles: profileData || undefined,
            like_count: reactionData.like_count,
            empathy_count: reactionData.empathy_count,
            user_liked: userReactions.liked,
            user_empathized: userReactions.empathized,
          };
        })
      );
      
      // DB返信とモック返信をマージ（モックにしかない返信も表示）
      const mockRepliesForPost = mockReplies.filter(r => r.post_id === id);
      const dbReplyIds = new Set(data.map(r => r.id));
      const missingMockReplies = await Promise.all(
        mockRepliesForPost
          .filter(m => !dbReplyIds.has(m.id))
          .map(async (reply) => {
            const reactionData = await fetchReactionCounts(reply.id);
            const userReactions = user ? await fetchUserReactions(reply.id) : { liked: false, empathized: false };
            return {
              ...reply,
              like_count: reactionData.like_count,
              empathy_count: reactionData.empathy_count,
              user_liked: userReactions.liked,
              user_empathized: userReactions.empathized,
            };
          })
      );
      
      setReplies([...missingMockReplies, ...repliesWithProfiles]);
    } catch (error) {
      console.error('返信の取得エラー:', error);
      // エラーの場合もモックデータを使用
      const mockRepliesForPost = mockReplies.filter(r => r.post_id === id);
      const mockWithReactions = mockRepliesForPost.map(reply => ({
        ...reply,
        like_count: Math.floor(Math.random() * 10),
        empathy_count: Math.floor(Math.random() * 15),
        user_liked: false,
        user_empathized: false,
      }));
      setReplies(mockWithReactions);
    }
  };

  // リアクション数を取得
  const fetchReactionCounts = async (replyId: string): Promise<{ like_count: number; empathy_count: number }> => {
    try {
      const { data, error } = await supabase
        .from('reply_reactions')
        .select('reaction_type')
        .eq('reply_id', replyId);

      if (error) throw error;

      const likeCount = data?.filter(r => r.reaction_type === 'like').length || 0;
      const empathyCount = data?.filter(r => r.reaction_type === 'empathy').length || 0;

      return { like_count: likeCount, empathy_count: empathyCount };
    } catch (error) {
      return { like_count: 0, empathy_count: 0 };
    }
  };

  // ユーザーのリアクション状態を取得
  const fetchUserReactions = async (replyId: string): Promise<{ liked: boolean; empathized: boolean }> => {
    if (!user) return { liked: false, empathized: false };

    try {
      const { data, error } = await supabase
        .from('reply_reactions')
        .select('reaction_type')
        .eq('reply_id', replyId)
        .eq('user_id', user.id);

      if (error) throw error;

      const liked = data?.some(r => r.reaction_type === 'like') || false;
      const empathized = data?.some(r => r.reaction_type === 'empathy') || false;

      return { liked, empathized };
    } catch (error) {
      return { liked: false, empathized: false };
    }
  };

  const checkEmpathy = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('empathy')
        .select('id')
        .eq('post_id', id)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setEmpathized(!!data);
    } catch (error) {
      console.error('共感チェックエラー:', error);
    }
  };

  const handleEmpathy = async () => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!isPostInDatabase) {
      alert('この投稿はサンプルデータのため、共感機能は利用できません。');
      return;
    }

    setSubmittingEmpathy(true);
    try {
      if (empathized) {
        // 共感を取り消す
        const { error: deleteError } = await supabase
          .from('empathy')
          .delete()
          .eq('post_id', id)
          .eq('user_id', user.id);

        if (deleteError) throw deleteError;

        // 投稿の共感数を減らす
        const { error: updateError } = await supabase
          .from('posts')
          .update({ empathy_count: empathyCount - 1 })
          .eq('id', id);

        if (updateError) throw updateError;

        setEmpathized(false);
        setEmpathyCount((prev) => prev - 1);
      } else {
        // 共感を追加
        const { error: insertError } = await supabase
          .from('empathy')
          .insert([{ post_id: id, user_id: user.id }]);

        if (insertError) throw insertError;

        // 投稿の共感数を増やす
        const { error: updateError } = await supabase
          .from('posts')
          .update({ empathy_count: empathyCount + 1 })
          .eq('id', id);

        if (updateError) throw updateError;

        setEmpathized(true);
        setEmpathyCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error('共感エラー:', error);
      alert('共感の処理に失敗しました。もう一度お試しください。');
    } finally {
      setSubmittingEmpathy(false);
    }
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      navigate('/auth/login');
      return;
    }

    if (!replyText.trim()) return;

    if (!isPostInDatabase) {
      alert('この投稿はサンプルデータのため、返信機能は利用できません。掲示板から新しい投稿を作成してください。');
      return;
    }

    setSubmittingReply(true);
    try {
      const { error } = await supabase.from('replies').insert([
        {
          post_id: id,
          user_id: user.id,
          content: replyText.trim(),
        },
      ]);

      if (error) {
        console.error('返信エラー:', error);
        alert('返信の投稿に失敗しました。もう一度お試しください。');
        setSubmittingReply(false);
        return;
      }

      setReplyText('');
      fetchReplies();
    } catch (error) {
      console.error('返信エラー:', error);
      alert('返信の投稿に失敗しました。もう一度お試しください。');
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleReplyReaction = async (replyId: string, reactionType: 'like' | 'empathy') => {
    if (!user) {
      navigate('/auth/login');
      return;
    }

    setReactionLoading(`${replyId}-${reactionType}`);
    
    try {
      const reply = replies.find(r => r.id === replyId);
      const isActive = reactionType === 'like' ? reply?.user_liked : reply?.user_empathized;

      if (isActive) {
        // リアクションを削除
        const { error } = await supabase
          .from('reply_reactions')
          .delete()
          .eq('reply_id', replyId)
          .eq('user_id', user.id)
          .eq('reaction_type', reactionType);

        if (error) throw error;
      } else {
        // リアクションを追加
        const { error } = await supabase
          .from('reply_reactions')
          .insert({
            reply_id: replyId,
            user_id: user.id,
            reaction_type: reactionType,
          });

        if (error) throw error;
      }

      // ローカル状態を更新
      setReplies(prev => prev.map(r => {
        if (r.id === replyId) {
          if (reactionType === 'like') {
            return {
              ...r,
              user_liked: !r.user_liked,
              like_count: r.user_liked ? (r.like_count || 1) - 1 : (r.like_count || 0) + 1,
            };
          } else {
            return {
              ...r,
              user_empathized: !r.user_empathized,
              empathy_count: r.user_empathized ? (r.empathy_count || 1) - 1 : (r.empathy_count || 0) + 1,
            };
          }
        }
        return r;
      }));
    } catch (error) {
      console.error('リアクションエラー:', error);
    } finally {
      setReactionLoading(null);
    }
  };

  const getCategoryStyle = (category: string) => {
    switch (category) {
      case 'つぶやき・モヤモヤ':
        return 'bg-rose-100 text-rose-700';
      case '教えて！ケアの知恵':
        return 'bg-amber-100 text-amber-700';
      case '保育園・社会参加':
        return 'bg-emerald-100 text-emerald-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-file-unknow-line text-6xl text-gray-300 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">投稿が見つかりません</h2>
          <Link to="/board" className="text-teal-600 hover:underline cursor-pointer">
            掲示板に戻る
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-purple-50 to-teal-50">
      {/* ナビゲーション */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-rose-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
                <i className="ri-ship-line text-white text-xl"></i>
              </div>
              <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-teal-600 to-teal-800 bg-clip-text text-transparent">
                みやぎスマイル・ポート
              </span>
            </Link>
            
            {/* デスクトップメニュー */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                ホーム
              </Link>
              <Link to="/board" className="text-teal-600 font-medium whitespace-nowrap cursor-pointer">
                みんなの休憩室
              </Link>
              <Link to="/manual" className="text-gray-600 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer">
                お守りマニュアル
              </Link>
              {user ? (
                <div className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 rounded-full">
                  <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                    <i className="ri-user-smile-line text-white text-sm"></i>
                  </div>
                  <span className="text-sm font-medium text-teal-700">{profile?.nickname || 'ユーザー'}</span>
                </div>
              ) : (
                <Link
                  to="/auth/login"
                  className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  ログイン
                </Link>
              )}
            </div>

            {/* モバイルハンバーガーメニュー */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer text-gray-900"
            >
              <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>

          {/* モバイルメニュー */}
          {isMobileMenuOpen && (
            <div className="md:hidden bg-white shadow-lg rounded-b-xl">
              <div className="px-4 py-3 space-y-3">
                <Link to="/" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">
                  ホーム
                </Link>
                <Link to="/board" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-teal-600 cursor-pointer">
                  みんなの休憩室
                </Link>
                <Link to="/manual" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">
                  お守りマニュアル
                </Link>
                {user ? (
                  <div className="flex items-center space-x-2 px-3 py-1.5 bg-teal-50 rounded-full">
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                      <i className="ri-user-smile-line text-white text-sm"></i>
                    </div>
                    <span className="text-sm font-medium text-teal-700">{profile?.nickname || 'ユーザー'}</span>
                  </div>
                ) : (
                  <Link
                    to="/auth/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full text-center cursor-pointer"
                  >
                    ログイン
                  </Link>
                )}
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* 心理的安全性メッセージ */}
      <div className="bg-gradient-to-r from-rose-100 to-purple-100 border-b border-rose-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-center space-x-2 sm:space-x-3">
            <i className="ri-shield-check-line text-rose-500 text-xl sm:text-2xl"></i>
            <p className="text-gray-700 text-center text-xs sm:text-base">
              <strong>ここはあなたの避難所です</strong> ・ 否定されることはありません ・ 安心してお話しください
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* パンくずリスト */}
        <div className="flex items-center flex-wrap space-x-2 text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
          <Link to="/" className="hover:text-teal-600 cursor-pointer">
            ホーム
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/board" className="hover:text-teal-600 cursor-pointer">
            みんなの休憩室
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-800 truncate max-w-[150px] sm:max-w-xs">{post?.title}</span>
        </div>

        {/* 投稿詳細 */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8 mb-4 sm:mb-6">
          <div className="flex items-start justify-between mb-3 sm:mb-4 flex-wrap gap-2">
            <div className="flex items-center space-x-2 flex-wrap gap-2">
              <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getCategoryStyle(post.category)}`}>
                {post.category}
              </span>
              {post.need_partner_response && (
                <span className="px-2 sm:px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs sm:text-sm font-medium flex items-center space-x-1">
                  <i className="ri-user-star-line"></i>
                  <span className="hidden sm:inline">パートナー回答希望</span>
                  <span className="sm:hidden">回答希望</span>
                </span>
              )}
            </div>
            <span className="text-xs sm:text-sm text-gray-500">{new Date(post.created_at).toLocaleString('ja-JP')}</span>
          </div>

          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-3 sm:mb-4">{post.title}</h1>
          <p className="text-gray-700 text-sm sm:text-base lg:text-lg leading-relaxed mb-4 sm:mb-6 whitespace-pre-wrap">{post.content}</p>

          <div className="flex items-center justify-between pt-4 sm:pt-6 border-t border-gray-200 flex-wrap gap-3 sm:gap-4">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              {post.profiles?.avatar_url ? (
                <img
                  src={post.profiles.avatar_url}
                  alt={post.profiles.nickname || '投稿者'}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
                />
              ) : (
                <div
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                    post.profiles?.user_type === 'partner' ? 'bg-gradient-to-br from-purple-400 to-purple-600' : 'bg-gradient-to-br from-teal-400 to-teal-600'
                  }`}
                >
                  <i
                    className={`text-white text-lg sm:text-xl ${
                      post.profiles?.user_type === 'partner' ? 'ri-user-star-line' : 'ri-user-smile-line'
                    }`}
                  ></i>
                </div>
              )}
              <div className="min-w-0">
                <div className="flex items-center space-x-2 mb-2 flex-wrap gap-1">
                  <p className="font-medium text-gray-800 text-sm sm:text-base">{post.profiles?.nickname || '匿名'}</p>
                  {post.profiles?.user_type === 'partner' && post.profiles?.partner_role && (
                    <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs">{post.profiles.partner_role}</span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-gray-500">投稿者</p>
              </div>
            </div>

            <button
              onClick={handleEmpathy}
              disabled={submittingEmpathy}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base ${
                empathized ? 'bg-rose-500 text-white shadow-lg' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'
              }`}
            >
              <div className="flex items-center space-x-2">
                <i className={empathized ? 'ri-heart-3-fill' : 'ri-heart-3-line'}></i>
                <span>わかって！</span>
                <span className="font-bold">{empathyCount}</span>
              </div>
            </button>
          </div>
        </div>

        {/* 返信セクション */}
        <div className="bg-white rounded-2xl shadow-lg p-4 sm:p-6 lg:p-8">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-4 sm:mb-6 flex items-center space-x-2">
            <i className="ri-chat-3-line text-teal-600"></i>
            <span>返信 ({replies.length})</span>
          </h2>

          {/* 返信一覧 */}
          <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
            {replies.length > 0 ? (
              replies.map((reply) => {
                // パートナーの場合、プロフィールページへのリンク用IDを取得
                const isPartner = reply.profiles?.user_type === 'partner';
                const partnerData = isPartner 
                  ? mockPartners.find(p => 
                      p.nickname === reply.profiles?.nickname && 
                      p.partner_role === reply.profiles?.partner_role
                    )
                  : null;
                
                return (
                  <div
                    key={reply.id}
                    className={`p-4 sm:p-6 rounded-xl ${isPartner ? 'bg-purple-50 border-2 border-purple-200' : 'bg-gray-50'}`}
                  >
                    <div className="flex items-start space-x-3 sm:space-x-4">
                      {/* アバター画像 */}
                      {partnerData ? (
                        <Link
                          to={`/partners/${partnerData.id}`}
                          className="flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                        >
                          {reply.profiles?.avatar_url ? (
                            <img
                              src={reply.profiles.avatar_url}
                              alt={reply.profiles.nickname || '返信者'}
                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                            />
                          ) : (
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-purple-600">
                              <i className="text-white text-lg sm:text-xl ri-user-star-line"></i>
                            </div>
                          )}
                        </Link>
                      ) : (
                        reply.profiles?.avatar_url ? (
                          <img
                            src={reply.profiles.avatar_url}
                            alt={reply.profiles.nickname || '返信者'}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div
                            className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                              isPartner ? 'bg-gradient-to-br from-purple-400 to-purple-600' : 'bg-gradient-to-br from-gray-400 to-gray-600'
                            }`}
                          >
                            <i
                              className={`text-white text-lg sm:text-xl ${
                                isPartner ? 'ri-user-star-line' : 'ri-user-smile-line'
                              }`}
                            ></i>
                          </div>
                        )
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2 mb-2 flex-wrap gap-1">
                          {/* 名前 */}
                          {partnerData ? (
                            <Link 
                              to={`/partners/${partnerData.id}`}
                              className="font-medium text-gray-800 text-sm sm:text-base hover:text-purple-600 transition-colors cursor-pointer"
                            >
                              {reply.profiles?.nickname || '匿名'}
                            </Link>
                          ) : (
                            <p className="font-medium text-gray-800 text-sm sm:text-base">{reply.profiles?.nickname || '匿名'}</p>
                          )}
                          
                          {/* 役職バッジ */}
                          {isPartner && reply.profiles?.partner_role && (
                            partnerData ? (
                              <Link
                                to={`/partners/${partnerData.id}`}
                                className="px-2 py-1 bg-purple-200 text-purple-800 rounded-full text-xs font-medium flex items-center space-x-1 hover:bg-purple-300 transition-colors cursor-pointer"
                              >
                                <i className="ri-shield-star-line"></i>
                                <span>{reply.profiles.partner_role}</span>
                              </Link>
                            ) : (
                              <span className="px-2 py-1 bg-purple-200 text-purple-800 rounded-full text-xs font-medium flex items-center space-x-1">
                                <i className="ri-shield-star-line"></i>
                                <span>{reply.profiles.partner_role}</span>
                              </span>
                            )
                          )}
                          
                          <span className="text-xs sm:text-sm text-gray-500">{new Date(reply.created_at).toLocaleString('ja-JP')}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm sm:text-base">{reply.content}</p>
                        
                        {/* リアクションボタン */}
                        <div className="flex items-center space-x-4 mt-3 pt-3 border-t border-gray-200/50">
                          <button
                            onClick={() => handleReplyReaction(reply.id, 'like')}
                            disabled={reactionLoading === `${reply.id}-like`}
                            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm transition-all cursor-pointer disabled:opacity-50 ${
                              reply.user_liked
                                ? 'bg-rose-100 text-rose-600'
                                : 'bg-gray-100 text-gray-600 hover:bg-rose-50 hover:text-rose-500'
                            }`}
                          >
                            <i className={`${reply.user_liked ? 'ri-heart-fill' : 'ri-heart-line'}`}></i>
                            <span>いいね</span>
                            {(reply.like_count || 0) > 0 && (
                              <span className="font-medium">{reply.like_count}</span>
                            )}
                          </button>
                          
                          <button
                            onClick={() => handleReplyReaction(reply.id, 'empathy')}
                            disabled={reactionLoading === `${reply.id}-empathy`}
                            className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-xs sm:text-sm transition-all cursor-pointer disabled:opacity-50 ${
                              reply.user_empathized
                                ? 'bg-amber-100 text-amber-600'
                                : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-500'
                            }`}
                          >
                            <i className={`${reply.user_empathized ? 'ri-emotion-happy-fill' : 'ri-emotion-happy-line'}`}></i>
                            <span>共感</span>
                            {(reply.empathy_count || 0) > 0 && (
                              <span className="font-medium">{reply.empathy_count}</span>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 sm:py-8 text-gray-500">
                <i className="ri-chat-off-line text-3xl sm:text-4xl mb-2"></i>
                <p className="text-sm sm:text-base">まだ返信がありません</p>
                <p className="text-xs sm:text-sm mt-1">最初の返信を書いてみませんか？</p>
              </div>
            )}
          </div>

          {/* 返信フォーム */}
          <div className="border-t border-gray-200 pt-4 sm:pt-6">
            <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3 sm:mb-4">返信を書く</h3>
            {user ? (
              <form onSubmit={handleReplySubmit}>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                  rows={4}
                  placeholder="共感の言葉や、あなたの経験を共有してください..."
                  className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none mb-2 text-sm sm:text-base"
                  maxLength={500}
                ></textarea>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <p className="text-xs text-gray-500">{replyText.length}/500</p>
                  <button
                    type="submit"
                    disabled={submittingReply || !replyText.trim()}
                    className="px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:shadow-lg transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
                  >
                    {submittingReply ? '投稿中...' : '返信を投稿'}
                  </button>
                </div>
              </form>
            ) : (
              <div className="text-center py-4 sm:py-6 bg-gray-50 rounded-xl">
                <p className="text-gray-600 mb-3 text-sm sm:text-base">返信するにはログインが必要です</p>
                <Link
                  to="/auth/login"
                  className="inline-block px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-xl hover:shadow-lg transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  ログインする
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* 戻るボタン */}
        <div className="mt-6 sm:mt-8 text-center">
          <Link
            to="/board"
            className="inline-flex items-center space-x-2 px-4 sm:px-6 py-2 sm:py-3 bg-white text-gray-700 rounded-xl hover:shadow-md transition-all cursor-pointer text-sm sm:text-base"
          >
            <i className="ri-arrow-left-line"></i>
            <span>掲示板に戻る</span>
          </Link>
        </div>
      </div>

      {/* 関連リンク */}
      <RelatedLinks />

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div>
              <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-ship-line text-white text-xl"></i>
                </div>
                <span className="text-lg sm:text-xl font-bold">みやぎスマイル・ポート</span>
              </div>
              <p className="text-teal-100 text-xs sm:text-sm">医療的ケア児と家族の港</p>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">リンク</h4>
              <ul className="space-y-2 text-xs sm:text-sm text-teal-100">
                <li>
                  <Link to="/" className="hover:text-white transition-colors cursor-pointer">
                    ホーム
                  </Link>
                </li>
                <li>
                  <Link to="/board" className="hover:text-white transition-colors cursor-pointer">
                    みんなの休憩室
                  </Link>
                </li>
                <li>
                  <Link to="/manual" className="hover:text-white transition-colors cursor-pointer">
                    お守りマニュアル
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-2 sm:mb-3 text-sm sm:text-base">お問い合わせ</h4>
              <p className="text-xs sm:text-sm text-teal-100">support@smile-port.jp</p>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-6 sm:pt-8 text-center text-xs sm:text-sm text-teal-100">
            <p>
              &copy; 2025 みやぎスマイル・ポート. All rights reserved. |{' '}
              <a
                href="https://readdy.ai/?ref=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
